import axios from 'axios';
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Table } from "react-bootstrap";
import ListGroup from 'react-bootstrap/ListGroup';
import getAPI from '../../service/getAPI';
import TableLoader from '../widgets/TableLoader';
import ViewDetailsModal from '../widgets/ViewDetailsModal';

const Undelivered = () => {
    const { master } = getAPI();
    const [list, setList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalBody, setModalBody] = useState("");
    const [filteredList, setFilteredList] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [globalSearch, setGlobalSearch] = useState("");
    const [statusOptions] = useState(["UNDELIVERED", "IN TRANSIT", "OUT FOR DELIVERY"]);
    const [courierList, setCourierList] = useState([]);
    
    // Extract unique couriers from the data
    const updateCourierList = useCallback((data) => {
        if (data && data.length > 0) {
            const couriers = new Set();
            data.forEach(item => {
                if (item.shipping_courier) {
                    couriers.add(item.shipping_courier.trim());
                }
            });
            setCourierList(Array.from(couriers).sort());
        }
    }, []);
    
    const [pagination, setPagination] = useState({
        totalRecords: 0,
        totalPages: 1,
        currentPage: 1
    });

    const [filters, setFilters] = useState({
        order_code: "",
        customer_name: "",
        shipping_courier: "",
        tracking_number: "",
        phone_number: "",
        courier_status: "UNDELIVERED",
    });

    const [dateFilter, setDateFilter] = useState({
        fromDate: "",
        toDate: "",
    });

    const itemsPerPage = 30; // Show 30 items per page

    useEffect(() => {
        console.log('useEffect triggered', { 
            currentPage: pagination.currentPage, 
            dateFilter,
            filters
        });
        loadData();
    }, [pagination.currentPage, dateFilter, filters]);

    useEffect(() => {
        console.log('Global search or filters changed:', { globalSearch, filters });
        
        let filteredData = [...list];
        
        // Apply global search if present
        if (globalSearch.trim()) {
            const searchTerm = globalSearch.toLowerCase().trim();
            filteredData = filteredData.filter(item => {
                return Object.entries(item).some(([key, value]) => {
                    // Skip certain fields that we don't want to search
                    if (['id', 'unicommerce_detail_id'].includes(key)) return false;
                    
                    // Handle null/undefined values
                    if (value === null || value === undefined) return false;
                    
                    // Convert value to string and search
                    return String(value).toLowerCase().includes(searchTerm);
                });
            });
        }
        
        // Apply other filters
        filteredData = applyFilters(filteredData);
        
        console.log('Filtered data:', filteredData);
        setFilteredList(filteredData);
    }, [globalSearch, list, filters]);

    const loadData = async () => {
        console.log('Loading data...', { 
            endpoint: master.getUndeliveredList,
            page: pagination.currentPage,
            limit: itemsPerPage,
            fromDate: dateFilter.fromDate,
            toDate: dateFilter.toDate,
            ...filters 
        });
        
        setIsLoading(true);
        try {
            const response = await axios.post(master.getUndeliveredList, {
                page: pagination.currentPage,
                limit: itemsPerPage,
                fromDate: dateFilter.fromDate || null,
                toDate: dateFilter.toDate || null,
                ...filters
            });

            console.log('API Response:', response.data);

            console.log('Raw API response:', response.data);
            
            // Handle different possible response structures
            let orders = [];
            let total = 0;
            
            if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
                // Handle case where data is nested under response.data.data.data
                orders = response.data.data.data;
                total = response.data.data.pagination?.totalRecords || orders.length;
            } else if (Array.isArray(response.data?.data)) {
                // Handle case where data is directly in response.data.data
                orders = response.data.data;
                total = orders.length;
            } else if (Array.isArray(response.data)) {
                // Handle case where data is directly the response
                orders = response.data;
                total = orders.length;
            }
            
            console.log('Processed orders:', { orders, total });
            
            setList(orders);
            setFilteredList(orders);
            
            // Update courier list with unique values
            updateCourierList(orders);
            
            // Calculate total pages based on items per page
            const totalPages = Math.ceil(total / itemsPerPage) || 1;
                
            setPagination(prev => ({
                ...prev,
                totalRecords: total,
                totalPages: totalPages,
                currentPage: Math.min(prev.currentPage, totalPages || 1)
            }));
        } catch (err) {
            console.error("Error loading undelivered orders:", err);
            setList([]);
            setFilteredList([]);
            setPagination(prev => ({
                ...prev,
                totalRecords: 0,
                totalPages: 1,
                currentPage: 1
            }));
        } finally {
            setIsLoading(false);
        }
    };

    const downloadCSV = () => {
        const headers = Object.keys(filters).join(",") + "\n";
        let newList = JSON.parse(JSON.stringify([...list]));
        if (selectedRows.length > 0) {
            newList = [...newList].filter(item => selectedRows.includes(item.order_code));
        }
        
        const rows = [
            Object.keys(filters).join(","),
            ...newList.map(row =>
                Object.keys(filters).map(key => row[key] ?? "").join(",")
            )
        ].join("\n");

        const csvContent = headers + rows;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `undelivered_orders_${new Date().toISOString()}.csv`;
        link.click();

        URL.revokeObjectURL(url);
    }

    const handleReset = () => {
        // Reset all filters to their initial state
        setFilters({
            order_code: "",
            order_date: "",
            customer_name: "",
            shipping_courier: "",
            tracking_number: "",
            phone_number: "",
            channel: "",
            courier_status: "UNDELIVERED"
        });
        
        // Reset date filter
        setDateFilter({
            fromDate: "",
            toDate: ""
        });
        
        // Reset global search
        setGlobalSearch("");
        
        // Reset pagination to first page
        setPagination({
            totalRecords: 0,
            totalPages: 1,
            currentPage: 1
        });
        
        // Reload data without filters
        loadData();
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const isCourierFilter = name === 'shipping_courier';
        
        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            
            // If courier filter changed, reset to page 1
            if (isCourierFilter) {
                setPagination(prev => ({
                    ...prev,
                    currentPage: 1
                }));
            }
            
            return newFilters;
        });
    };

    const applyFilters = (data) => {
        let filteredData = [...data];

        Object.keys(filters).forEach((key) => {
            const filterValue = filters[key].trim();
            if (!filterValue) return;

            filteredData = filteredData.filter((item) => {
                const itemValue = item[key];
                if (itemValue === null || itemValue === undefined) return false;

                if (key === "order_date") {
                    const formattedDate = new Date(itemValue).toLocaleDateString("en-IN");
                    return formattedDate.toLowerCase().includes(filterValue.toLowerCase());
                }

                return itemValue.toString().toLowerCase().includes(filterValue.toLowerCase());
            });
        });

        // Update pagination based on filtered results
        const totalRecords = filteredData.length;
        const totalPages = Math.ceil(totalRecords / itemsPerPage) || 1;
        
        setPagination(prev => ({
            ...prev,
            totalRecords,
            totalPages,
            currentPage: Math.min(prev.currentPage, totalPages || 1)
        }));
        
        return filteredData;

        setFilteredList(filteredData);
        return filteredData;
    };

    useEffect(() => {
        setFilteredList(applyFilters(list));
    }, [filters, list]);

    const handleCheckboxChange = (id) => {
        setSelectedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedRows.length === currentItems.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows(currentItems.map((item) => item.order_code));
        }
    };



    const handleModalData = (order) => {
        let status = order?.courier_status?.toUpperCase() || "N/A";
        let trackingData = "";
        let trackingUrl = order?.tracking_url;
        
        if (order?.tracking_array) {
            let trackingJson = [];
            try {
                trackingJson = JSON.parse(order?.tracking_array);
                trackingJson?.sort((a, b) => new Date(b.date) - new Date(a.date));
                if (trackingJson.length > 0) {
                    trackingData = (
                        <ListGroup className='mb-3'>
                            {trackingJson?.map((row, idx) => (
                                <div className='row' key={idx}>
                                    <ListGroup.Item>
                                        <span className='badge text-bg-light'>
                                            {new Date(row?.date).toLocaleDateString("en-IN")} {new Date(row?.date).toLocaleTimeString()}
                                        </span>
                                        <br />
                                        <span className='ms-2'><strong>Location:</strong> {row?.location}</span>
                                        <br />
                                        <span className='ms-2'><strong>Activity:</strong> {row?.activity}</span>
                                        <br />
                                    </ListGroup.Item>
                                </div>
                            ))}
                        </ListGroup>
                    );
                }
            } catch (error) {
                console.error("Error parsing tracking data:", error);
            }
        }

        const body = (
            <>
                <p>
                    <strong className='ms-2'>Status: </strong>
                    <span className='text-primary'>{status}</span>
                </p>
                <p className='ms-2'>
                    Customer Tracking URL: <a target='_blank' rel="noreferrer" href={`/track/${order?.tracking_number}`}>Click here</a>
                </p>
                <div>{trackingData}</div>
                {trackingUrl && (
                    <div className='text-center mt-3'>
                        <p>For more Information: <a target='_blank' rel="noreferrer" href={trackingUrl}>Click here</a></p>
                    </div>
                )}
            </>
        );

        setModalBody(body);
        setShowModal(true);
    };

    // Apply pagination to filtered list
    const currentItems = filteredList.slice(
        (pagination.currentPage - 1) * itemsPerPage,
        pagination.currentPage * itemsPerPage
    );
    const { totalPages, currentPage: currentPageNum, totalRecords } = pagination;

    return (
        <main id="main" className="main">
            <div className="pagetitle">
                <h1>Undelivered Orders</h1>
            </div>

            <div className="row mt-3">
                <div className="col-md-12">
                    <div className="card">
                        <div className="card-body pt-3">
                        <div className="row mb-3">
                            <div className="col-md-2">
                                <label>From Date:</label>
                                <Form.Control
                                    type="date"
                                    value={dateFilter.fromDate}
                                    onChange={(e) =>
                                        setDateFilter({ ...dateFilter, fromDate: e.target.value })
                                    }
                                />
                            </div>
                            <div className="col-md-2">
                                <label>To Date:</label>
                                <Form.Control
                                    type="date"
                                    value={dateFilter.toDate}
                                    onChange={(e) =>
                                        setDateFilter({ ...dateFilter, toDate: e.target.value })
                                    }
                                />
                            </div>
                            <div className="col-md-4 d-flex align-items-end">
                                <Button
                                    variant="primary"
                                    onClick={() => loadData({ ...dateFilter })}
                                    className="me-2"
                                >
                                    Submit
                                </Button>
                                <Button
                                    variant="secondary"
                                    onClick={handleReset}
                                    className="me-2"
                                >
                                    Reset
                                </Button>
                                <Button
                                    variant="success"
                                    onClick={downloadCSV}
                                    className="me-2"
                                >
                                    Export CSV
                                </Button>
                            </div>
                            <div className="col-md-4 d-flex align-items-end gap-2">
                                <Form.Select
                                    size="sm"
                                    style={{ width: '180px' }}
                                    name="shipping_courier"
                                    value={filters.shipping_courier || ''}
                                    onChange={handleFilterChange}
                                    title="Filter by courier"
                                >
                                    <option value="">All Couriers ({courierList.length})</option>
                                    {courierList.map((courier, index) => (
                                        <option key={index} value={courier}>
                                            {courier}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Control
                                    type="text"
                                    placeholder="Search orders..."
                                    value={globalSearch}
                                    onChange={e => setGlobalSearch(e.target.value)}
                                    size="sm"
                                />
                            </div>
                        </div>

                        <Table striped bordered hover responsive>
                            <thead>
                                <tr className="table-secondary">
                                    <th>
                                        <Form.Check
                                            type="checkbox"
                                            checked={
                                                selectedRows.length === currentItems.length &&
                                                currentItems.length > 0
                                            }
                                            onChange={handleSelectAll}
                                        />
                                    </th>
                                    <th>No.</th>
                                    <th>Order Code</th>
                                    <th>Order Date</th>
                                    <th>Customer</th>
                                    <th>Shipping Courier</th>
                                    <th>Tracking Number</th>
                                    <th>Channel</th>
                                    <th>Phone Number</th>
                                    <th>Status</th>
                                </tr>
                                <tr>
                                    <th></th>
                                    <th></th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter order code"
                                            name="order_code"
                                            value={filters.order_code}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                    <Form.Control
                                            type="text"
                                            placeholder="Filter order date"
                                            name="order_date"
                                            value={filters.order_date}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter customer"
                                            name="customer_name"
                                            value={filters.customer_name}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter courier"
                                            name="shipping_courier"
                                            value={filters.shipping_courier}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter tracking"
                                            name="tracking_number"
                                            value={filters.tracking_number}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter channel"
                                            name="channel"
                                            value={filters.channel}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    <th>
                                        <Form.Control
                                            type="text"
                                            placeholder="Filter phone"
                                            name="phone_number"
                                            value={filters.phone_number}
                                            onChange={handleFilterChange}
                                            size="sm"
                                        />
                                    </th>
                                    {/* <th>
                                        <Form.Select
                                            size="sm"
                                            value={filters.courier_status}
                                            onChange={e => handleFilterChange({ target: { name: 'courier_status', value: e.target.value } })}
                                        >
                                            <option value="">All Status</option>
                                            {statusOptions.map((status, index) => (
                                                <option key={index} value={status}>
                                                    {status}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </th> */}
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan="12" className="text-center">
                                            <TableLoader />
                                        </td>
                                    </tr>
                                ) : (
                                    <>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((order, index) => (
                                                <tr key={order.unicommerce_detail_id || index}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={selectedRows.includes(order.order_code)}
                                                            onChange={() => handleCheckboxChange(order.order_code)}
                                                        />
                                                    </td>
                                                    <td>{((pagination.currentPage - 1) * itemsPerPage) + index + 1}</td>
                                                    <td>{order.order_code || "-"}</td>
                                                    <td>
                                                        {order.order_date ? new Date(order.order_date).toLocaleDateString("en-IN") : "-"}
                                                    </td>
                                                    <td>{order.customer_name || "-"}</td>
                                                    <td>{order.shipping_courier || "-"}</td>
                                                    <td>
                                                        {order.tracking_number ? (
                                                            <a href={order.tracking_url} target="_blank" rel="noopener noreferrer">
                                                                {order.tracking_number}
                                                            </a>
                                                        ) : "-"}
                                                    </td>
                                                    <td>{order.channel || "-"}</td>
                                                    <td>{order.phone_number || "-"}</td>
                                                    <td>
                                                        <span 
                                                            className={`badge ${order.courier_status === 'DELIVERED' ? 'bg-success' : 'bg-warning'}`}
                                                        >
                                                            {order.courier_status || "PENDING"}
                                                        </span>
                                                        {order.tracking_array && order.tracking_array !== "[]" && (
                                                            <Button 
                                                                variant="link" 
                                                                size="sm" 
                                                                onClick={() => handleModalData(order)}
                                                                className="p-0 ms-2"
                                                            >
                                                                View Details
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="12" className="text-center">
                                                    No undelivered orders found
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                )}
                            </tbody>
                        </Table>

                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted">
                                Showing {Math.min((pagination.currentPage - 1) * itemsPerPage + 1, pagination.totalRecords)} to {Math.min(pagination.currentPage * itemsPerPage, pagination.totalRecords)} of {pagination.totalRecords} records
                            </div>
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                                        window.scrollTo(0, 0);
                                    }}
                                    disabled={pagination.currentPage === 1}
                                >
                                    First
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setPagination(prev => ({
                                            ...prev,
                                            currentPage: Math.max(1, prev.currentPage - 1)
                                        }));
                                        window.scrollTo(0, 0);
                                    }}
                                    disabled={pagination.currentPage === 1}
                                >
                                    Previous
                                </Button>
                                <span className="mx-2 align-self-center">
                                    Page {pagination.currentPage} of {pagination.totalPages}
                                </span>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setPagination(prev => ({
                                            ...prev,
                                            currentPage: Math.min(prev.totalPages, prev.currentPage + 1)
                                        }));
                                        window.scrollTo(0, 0);
                                    }}
                                    disabled={pagination.currentPage >= pagination.totalPages}
                                >
                                    Next
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                        setPagination(prev => ({
                                            ...prev,
                                            currentPage: prev.totalPages
                                        }));
                                        window.scrollTo(0, 0);
                                    }}
                                    disabled={pagination.currentPage >= pagination.totalPages}
                                >
                                    Last
                                </Button>
                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <ViewDetailsModal
                title="Order Tracking Details"
                body={modalBody}
                show={showModal}
                setShow={setShowModal}
                size="lg"
            />
        </main>
    );
};

export default Undelivered;